<script setup>
import { computed, ref } from 'vue';
import { useField } from 'vee-validate';
import Multiselect from 'vue-multiselect';
import { useI18n } from 'vue-i18n';
import { sortArray } from 'utils';

const { t } = useI18n();

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  initialValue: {
    type: [String, Object, null],
    required: false,
    default: null,
  },
  label: {
    type: String,
    required: false,
    default: null
  },
  options: {
    type: [Object, Array],
    required: true
  },
  showEmptyDefault: {
    type: Boolean,
    required: false,
    default: false
  },
  validatorName: {
    type: String,
    required: false,
    default: null,
  },
  rules: {
    type: String,
    required: false,
    default: null
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  showRequiredIndicator: {
    type: Boolean,
    required: false,
    default: true
  },
  showOtherOption: {
    type: Boolean,
    required: false,
    default: false
  },
  forceRequiredForOtherOption: {
    type: Boolean,
    required: false,
    default: false
  },
  useKeyOnlyValue: {
    type: Boolean,
    required: false,
    default: true,
  },
  searchable: {
    type: Boolean,
    required: false,
    default: false,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  }
});

const emit = defineEmits(['input']);

const useFieldOpts = {
  label: props.validatorName ?? props.label,
  validateOnValueUpdate: false,
};

if (props.initialValue) {
  useFieldOpts.initialValue = props.initialValue;
}

const {
  errorMessage,
  setValue,
  value,
} = useField(props.name, props.rules, useFieldOpts);

const otherOptionActive = computed(() => props.showOtherOption
  && value.value
  && typeof value.value === 'object'
  && value.value.value === 'other');

const other = ref(otherOptionActive.value ? value.value.customInput : null);

const isRequired = computed(() => props.rules && props.rules.indexOf('required') !== -1);

const otherRules = computed(() => {
  if (props.forceRequiredForOtherOption && !isRequired.value) {
    return props.rules ? `${props.rules} required` : 'required';
  }

  return props.rules;
});
const isOtherRequired = computed(() => otherRules.value && otherRules.value.indexOf('required') !== -1);

const shouldEmitFullValue = computed(() => !props.useKeyOnlyValue || props.showOtherOption);

const preparedOptions = computed(() => {
  const options = Array.isArray(props.options)
    ? [...props.options]
    : Object.keys(props.options).map((v) => ({ title: props.options[v], value: v }));

  if (props.showOtherOption) {
    options.push({ title: t('generic.otherOption'), value: 'other' });
  }

  return sortArray(options, 'title', 'asc');
});

const preparedValue = computed(() => {
  if (value.value) {
    if (typeof value.value === 'object') {
      return value.value;
    }

    if (props.options[value.value]) {
      return {
        title: props.options[value.value],
        value: value.value
      };
    }
  }

  return null;
});

function handleInput(event) {
  if (shouldEmitFullValue.value) {
    const payload = {
      ...event,
    };

    if (props.showOtherOption && payload.value === 'other') {
      payload.customInput = other.value;
    }

    emit('input', payload);

    setValue(payload, true);
  } else {
    emit('input', event && event.value ? event.value : null);
    setValue(event && event.value ? event.value : null, true);
  }
}

function handleCustomInput(otherValue) {
  other.value = otherValue;

  setValue({
    ...value.value,
    customInput: otherValue,
  }, true);
}
</script>

<template>
  <div
    class="ce-field ce-selectField"
    :class="{'ce-selectField--withOtherOption': otherOptionActive}">
    <div>
      <label
        v-if="label"
        :for="name">
        {{ label }} <span v-if="isRequired && showRequiredIndicator">*</span>
        <span
          v-if="errorMessage"
          class="ms-2 ml1 regular ce-colorError">
          {{ errorMessage }}
        </span>
      </label>

      <div class="ce-selectField-fieldWrapper">
        <div>
          <multiselect
            :id="name"
            :name="name"
            :model-value="preparedValue"
            :options="preparedOptions"
            label="title"
            track-by="value"
            :searchable="searchable"
            :close-on-select="true"
            :allow-empty="showEmptyDefault"
            placeholder=""
            @update:modelValue="handleInput"
            :disabled="disabled"
            :loading="loading" />
        </div>
      </div>
    </div>

    <div v-if="otherOptionActive">
      <label v-if="label">
        {{ $t('generic.otherOption') }} <span v-if="isOtherRequired && showRequiredIndicator">*</span>
        <span
          v-if="errorMessage"
          class="ms-2 ml1 regular ce-colorError">
          {{ errorMessage }}
        </span>
      </label>
      <input
        type="text"
        :disabled="disabled"
        :value="other"
        @input="handleCustomInput($event.target.value)"
        autocomplete=""
        autocapitalize="">
    </div>
  </div>
</template>

<style lang="less">
@import (reference) '~styles/includes/base';

.ce-field.ce-selectField {
  .multiselect {
    .multiselect__tags {
      .multiselect__single {
        background: none;

        &:hover {
          cursor: pointer;
        }
      }
    }
  }

  &.ce-selectField--withOtherOption {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;

    > div {
      width: 100%;

      @media screen and (min-width: @breakpoint-xs) {
        &:first-child {
          width: 70%;
          padding-right: @space-2;
        }

        &:last-child {
          width: 30%;
          padding-left: @space-2;
        }
      }

      .ce-field {
        margin-bottom: 0;
      }
    }
  }
}
</style>
